import { useCallback, useState } from "react";

import { Button, Form, Input, PickerInput } from "../lib";
import {
  Language,
  LanguageLevel,
  User,
  useUserLanguagesUpdate,
} from "../services";
import { useInit } from "../services/epox/utils";

const initialLanguages: Language[] = [
  { code: "en", level: "" },
  { code: "zh", level: "" },
  { code: "jp", level: "" },
  { code: "ko", level: "" },
  { code: "de", level: "" },
  { code: "nl", level: "" },
  { code: "fr", level: "" },
  { code: "es", level: "" },
];

const languageNames: Record<string, string> = {
  en: "English",
  zh: "Chinese",
  jp: "Japanese",
  ko: "Korean",
  de: "German",
  nl: "Dutch",
  fr: "French",
  es: "Spanish",
};

type LanguageLevelOption = {
  label: string;
  value: LanguageLevel;
};

const LANGUAGE_LEVELS: LanguageLevelOption[] = [
  { label: "Don't speak", value: "" },
  { label: "Basic", value: "basic" },
  { label: "Average", value: "avg" },
  { label: "Fluent", value: "fluent" },
];

type Props = {
  user: User;
  onSuccess?: () => void;
};

export function LanguagesForm({ user, onSuccess }: Props) {
  // Data
  const [updateUserLanguages, isUpdating] = useUserLanguagesUpdate();

  // State
  const [languages, setLanguages] = useState<Language[]>(initialLanguages);

  // Handlers
  const handleChangeLanguage = useCallback(
    (name: string, value: LanguageLevel | null) => {
      if (value) {
        setLanguages((prev) =>
          prev.map((prevLang) =>
            prevLang.code === name ? { ...prevLang, level: value } : prevLang
          )
        );
      }
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    await updateUserLanguages({ languages });

    onSuccess?.();
  }, [languages, onSuccess, updateUserLanguages]);

  // Effects
  useInit(() => {
    setLanguages((prev) =>
      prev.map(
        (prevLang) =>
          user.languages.find((userLang) => userLang.code === prevLang.code) ??
          prevLang
      )
    );
  });

  // Render
  return (
    <Form>
      {languages.map(({ code, level }) => (
        <Input key={code} label={languageNames[code]}>
          <PickerInput
            name={code}
            value={level}
            options={LANGUAGE_LEVELS}
            onChange={handleChangeLanguage}
          />
        </Input>
      ))}

      <Button em onPress={handleSubmit} loading={isUpdating}>
        Save changes
      </Button>
    </Form>
  );
}
